import { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  IconButton,
  Chip, 
  Tooltip,
  Box,
  MenuItem
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ToggleOnIcon from "@mui/icons-material/ToggleOn"; 
import ToggleOffIcon from "@mui/icons-material/ToggleOff"; 
import API from "../api";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", role: "c" });

  // 🏢 Helper: Safely resolve current active Tenant ID matching global api.js expectations
  const getActiveTenantId = () => {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        const user = JSON.parse(userString);
        return user.tenant_id || "default";
      }
    } catch (e) {
      console.error("Error reading tenant context configuration profile:", e);
    }
    return localStorage.getItem("tenant_id") || "default";
  };

  // 🚀 Multi-Tenant Optimized: Request paths append tenant schema identifiers dynamically
  // 🎯 Ensure your fetch method is fully wrapped in useCallback to stop re-render races!
const fetchUsers = useCallback(async () => {
  try {
    const res = await API.get("users");
    
    // 🔍 THE FRONTEND GUARD: Check exactly how your backend sends data.
    // Console log it to see if it's a raw array or an object!
    console.log("📊 RAW BACKEND USER PAYLOAD:", res.data);

    if (Array.isArray(res.data)) {
      setUsers(res.data);
    } else if (res.data && Array.isArray(res.data.users)) {
      // If your backend nests it like {"users": [...]}
      setUsers(res.data.users);
    }
  } catch (err) {
    console.error("Failed to parse user rows into state:", err);
  }
}, []); // Empty array guarantees the reference remains stable

useEffect(() => {
  fetchUsers();
}, [fetchUsers]); // Safe, stable dependency tracking

  const handleOpenModal = (user = null) => {
    setEditingUser(user);
    if (user) {
      let defaultRole = user.role;
      if (defaultRole === "admin") defaultRole = "a";
      if (defaultRole === "supervisor") defaultRole = "s";
      if (defaultRole === "cashier") defaultRole = "c";

      setFormData({ name: user.name, email: user.email, password: "", role: defaultRole });
    } else {
      setFormData({ name: "", email: "", password: "", role: "c" });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingUser(null);
    setFormData({ name: "", email: "", password: "", role: "c" });
  };

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const safeName = (formData.name || "").trim();
      const safeEmail = (formData.email || "").trim();
      const safePassword = formData.password;

      if (!safeName || !safeEmail || (!editingUser && !safePassword)) {
        setError("Please complete all mandatory configuration field entries.");
        return;
      }

      const headers = { "X-Tenant-ID": getActiveTenantId() };

      if (editingUser) {
        const updatePayload = { 
          name: safeName,
          email: safeEmail.toLowerCase(),
          role: formData.role
        };
        
        if (safePassword && safePassword.trim().length > 0) {
          updatePayload.password = safePassword;
        }
        
        await API.put(`/users/${editingUser.id}`, updatePayload, { headers });
      } else {
        const createPayload = {
          name: safeName,
          email: safeEmail.toLowerCase(),
          password: safePassword,
          role: formData.role,
          is_active: true
        };
        await API.post("/users", createPayload, { headers });
      }

      fetchUsers();
      handleCloseModal();
    } catch (err) {
      console.error("Save failure:", err);
      setError(err.response?.data?.detail || "An internal error occurred while trying to save personnel records.");
    }
  };

  const handleToggleStatus = async (user) => {
    const action = user.is_active ? "deactivate" : "activate";
    const confirmationMsg = user.is_active 
      ? `Are you sure you want to suspend access permissions for ${user.name}?`
      : `Are you sure you want to restore active credentials for ${user.name}?`;

    if (!window.confirm(confirmationMsg)) return;

    try {
      await API.patch(`/users/${user.id}/${action}`, {}, {
        headers: { "X-Tenant-ID": getActiveTenantId() }
      });
      fetchUsers();
    } catch (err) {
      console.error("State alter error:", err);
      setError(err.response?.data?.detail || `Failed to cleanly switch staff state to ${action}.`);
    }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm("CRITICAL: Completely eliminate this staff account? This cannot be reversed.")) return;
    try {
      await API.delete(`/users/${id}`, {
        headers: { "X-Tenant-ID": getActiveTenantId() }
      });
      fetchUsers();
    } catch (err) {
      console.error("Hard delete crash:", err);
      setError(err.response?.data?.detail || "System rejected profile purge sequence.");
    }
  };

  const renderRoleChip = (roleCode) => {
    const parsed = String(roleCode).toLowerCase();
    if (parsed === "a" || parsed === "admin") return <Chip label="Admin" color="primary" variant="outlined" size="small" />;
    if (parsed === "s" || parsed === "supervisor") return <Chip label="Supervisor" color="secondary" variant="outlined" size="small" />;
    return <Chip label="Cashier" variant="outlined" size="small" />;
  };

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h2" fontWeight="600">
            Staff Operational Directory
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage employee system access, edit status profiles, or register new organization accounts.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} color="primary" onClick={() => handleOpenModal()}>
          Provision New Staff
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table aria-label="staff registry database table">
          <TableHead sx={{ backgroundColor: "action.hover" }}>
            <TableRow>
              <TableCell style={{ fontWeight: "bold" }}>ID</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>Personnel Name</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>System Email</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>Role Classification</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>Tenant Scope</TableCell> {/* 🚀 ADDED */}
              <TableCell style={{ fontWeight: "bold" }}>Duty Clearance Status</TableCell> 
              <TableCell style={{ fontWeight: "bold" }}>Date Enrolled</TableCell>
              <TableCell align="right" style={{ fontWeight: "bold" }}>Security Controls</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!Array.isArray(users) || users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No registered personnel records located inside the database.
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} sx={{ opacity: user.is_active ? 1 : 0.55, transition: "opacity 0.2s" }}> 
                  <TableCell sx={{ fontWeight: "500" }}>{user.id}</TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{renderRoleChip(user.role)}</TableCell>
                  
                  {/* 🚀 ADDED: Renders the backend multi-tenant context block identifier */}
                  <TableCell>
                    <Chip 
                      label={user.tenant_id || "default"} 
                      size="small" 
                      variant="outlined" 
                      sx={{ fontFamily: "monospace", letterSpacing: "0.5px", bgcolor: "rgba(0, 0, 0, 0.02)" }} 
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={user.is_active ? "Active Duty" : "On Leave / Suspended"}
                      color={user.is_active ? "success" : "warning"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' }) : "N/A"}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={user.is_active ? "Revoke Access" : "Authorize Status"}>
                      <IconButton onClick={() => handleToggleStatus(user)} size="large">
                        {user.is_active ? <ToggleOnIcon color="success" fontSize="large" /> : <ToggleOffIcon color="action" fontSize="large" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modify Base Registry Data">
                      <IconButton onClick={() => handleOpenModal(user)} color="default">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Permanent Registry Erase">
                      <IconButton onClick={() => handlePermanentDelete(user.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="xs">
        <DialogTitle fontWeight="600">{editingUser ? "Modify Personnel Account" : "Enroll System User"}</DialogTitle>
        <Box component="form" onSubmit={handleSave} noValidate>
          <DialogContent dividers>
            <TextField margin="normal" label="Full Name" fullWidth required autoFocus value={formData.name} onChange={handleInputChange("name")} />
            <TextField margin="normal" label="Email Address" type="email" fullWidth required value={formData.email} onChange={handleInputChange("email")} />
            <TextField select margin="normal" label="System Assignment Role" fullWidth required value={formData.role} onChange={handleInputChange("role")}>
              <MenuItem value="c">Cashier / Conductor Interface</MenuItem>
              <MenuItem value="s">Store Supervisor Clearance</MenuItem>
              <MenuItem value="a">System Administrator Dashboard</MenuItem>
            </TextField>
            <TextField
              margin="normal"
              label={editingUser ? "Override Password Token" : "Account Password String"}
              type="password"
              fullWidth
              required={!editingUser}
              value={formData.password}
              onChange={handleInputChange("password")}
              helperText={editingUser ? "Leave vacant to maintain existing secure hash sequence" : "Minimum 4 alphanumeric characters required"}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseModal} color="inherit">Dismiss</Button>
            <Button type="submit" variant="contained" color="primary">Commit Records</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar open={!!error} message={error} autoHideDuration={4000} onClose={() => setError("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
}